import React, { useRef, useEffect, useState, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { func, bool, string } from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  FormHelperText,
  OutlinedInput,
  Checkbox,
  Grid,
  styled,
} from '@nextiva/next-ui-components';

import {
  setAnnouncements,
  setAssignedAnnouncement,
  setGreetingToRemove,
} from 'containers/VoiceMail/reducers/options';
import {
  setOptionsFormRef,
  setOptionsFormReset,
} from 'containers/VoiceMail/reducers/formsState';
import Hint from 'components/VoicemailHint';
import GreetingRow from 'components/GreetingRow';
import * as regex from 'constants/regex';
import { HuntGroupVoicemailService } from 'services';
import sassVars from 'styles/application/_base/_variables.scss';
import { pxToRem } from 'utils/pxToRemConverter';
import { FeatureContext } from 'components/AccessResolvers/featureContext';

const { putHuntGroupVoicemailGreeting } = HuntGroupVoicemailService;

const Label = styled(FormControlLabel)`
  margin-right: 16px !important;
  .MuiTypography-body1 {
    font-family: Lato, sans-serif;
    font-size: ${pxToRem('14px')};
    line-height: 20px;
    font-weight: 500;
    letter-spacing: 0.21px;
  }
`;

const CheckboxStyled = styled(Checkbox)`
  &.MuiCheckbox-root {
    color: ${sassVars.secondaryColor};
  }
  &.MuiCheckbox-root.Mui-checked {
    color: ${sassVars.primaryBlue};
  }
  &.MuiCheckbox-colorSecondary.Mui-checked:hover {
    background-color: ${sassVars.secondaryColorHover};
  }
  &.MuiButtonBase-root {
    padding: 9px 9px 9px 0px;
  }
  &.MuiIconButton-colorSecondary:hover {
    background-color: ${sassVars.secondaryColorHover};
  }
  &.MuiTypography-body1 {
    font-size: ${pxToRem('14px')};
  }
`;

const GreetingsContainer = styled('div')`
  margin-bottom: 24px;
  font-size: ${pxToRem('14px')};
  &:hover {
    overflow-y: auto;
  }
`;

const TableHeader = styled('div')`
  padding: 0 0 16px 8px;
  margin: 48px 0 0;
  color: ${sassVars.secondaryColor};
  font-size: ${pxToRem('16px')};
  font-weight: 700;
  letter-spacing: 0.24px;
`;

const FormHeader = styled('div')`
  padding: 0 0 16px;
  margin: 48px 0 0;
  font-family: Lato, sans-serif;
  font-size: ${pxToRem('16px')};
  font-weight: 900;
  line-height: 24px;
  letter-spacing: 0.4px;
  color: ${sassVars.primaryFontColor};
`;

const FormControlStyled = styled(FormControl)`
  display: flex;
  flex-direction: column;
  margin-bottom: 5px;

  .MuiFormLabel-root {
    margin: 0 0 8px;
  }
`;

const HorizontalFormControl = styled(FormControl)`
  display: flex;
  flex-direction: row;
  margin-bottom: 5px;
  max-height: 40px;
  min-height: 40px;
  align-items: center;

  .MuiFormLabel-root {
    margin: 0 0 8px;
  }
`;

const textField = `
  font-family: Lato, sans-serif;
  font-weight: 400;
  & .MuiOutlinedInput-input {
    padding: 10.5px 14px 10.5px 14px;
  }
  &.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
    border-color: ${sassVars.selectBorderColor};
  }
  &.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${sassVars.primaryBlue};
    border-width: 1px;
  }
  & .MuiOutlinedInput-notchedOutline {
    border-color: ${sassVars.selectBorderColor};
  }
`;

const Pin = styled(OutlinedInput)`
  &.MuiOutlinedInput-root {
    width: 88px;
    margin: 0 16px 0 0;
  }
  ${textField}
`;

const Email = styled(OutlinedInput)`
  min-height: 40px;
  &.MuiOutlinedInput-root {
    width: 260px;
    margin: 0 16px 0 0;
    &.Mui-disabled {
      background: ${sassVars.dividerColor};
    }
  }
  ${textField}
`;

const GridStyled = styled(Grid)`
  margin-top: 15px;
`;

const HelperText = styled(FormHelperText)`
  height: 19px;
  color: ${sassVars.error};
  font-family: Lato, sans-serif;
  font-weight: 300;
  &.MuiFormHelperText-contained {
    margin-left: 0;
    margin-right: 0;
  }
`;

const OptionsForm = ({
  setOptionsIsDirty = () => {},
  setOptionsHasErrors = () => {},
  onSubmit = () => {},
  isVoicemailActive = false,
  huntGroupId = '',
  'data-testid': dataTestId,
}) => {
  const {
    control,
    setValue,
    handleSubmit,
    formState,
    formState: { errors },
    reset,
    getValues,
  } = useForm({
    defaultValues: {
      pinNumber: '••••',
      emailToCarbonCopy: '',
      notificationEmail: '',
      notificationEmailEnabled: false,
    },
  });
  const announcements = useSelector((state) => state.options.announcements);
  const assignedAnnouncement = useSelector(
    (state) => state.options.assignedAnnouncement,
  );
  const defaultAnnouncement = useSelector(
    (state) => state.options.defaultAnnouncement,
  );
  const response = useSelector((state) => state.options.response);
  const dispatch = useDispatch();
  const [emailRequired, setEmailRequired] = useState(false);
  const [isEmailEnabled, setIsEmailEnabled] = useState(false);

  const optionsFormRef = useRef(null);

  const {
    featureState: { isNewVoicemail },
  } = useContext(FeatureContext);

  useEffect(() => {
    dispatch(setOptionsFormReset(reset));
    dispatch(setOptionsFormRef(optionsFormRef));
  }, []);

  useEffect(() => {
    setOptionsIsDirty(formState.isDirty);
    const hasErrors = Object.keys(formState.errors).length > 0;
    setOptionsHasErrors(hasErrors);
  }, [formState]);

  useEffect(() => {
    if (!response) return;
    setValue('announcements', response.announcements);
    setValue('assignedAnnouncement', response.assignedAnnouncement);
    setValue('defaultAnnouncement', response.defaultAnnouncement);
    setValue('emailToCarbonCopy', response.emailToCarbonCopy);
    setValue('noAnswerNumberOfRings', response.noAnswerNumberOfRings);
    setValue('pinNumber', '••••');
    setValue('notificationEmailEnabled', response.notificationEmailEnabled);
    setValue(
      'notificationEmail',
      response.notificationEmail ? response.notificationEmail : '',
    );
    if (response.notificationEmailEnabled) setEmailRequired(true);
  }, [response]);

  const onSetUserGreeting = (greeting) => {
    let greetings = [...announcements];
    const index = greetings.findIndex(
      (g) => g.greetingName === greeting.greetingName,
    );
    greetings.splice(index, 1);
    greetings = [greeting, ...greetings];

    dispatch(setAssignedAnnouncement(greeting.greetingName));
    setValue('assignedAnnouncement', greeting.greetingName);

    dispatch(setAnnouncements(greetings));
    setValue('announcements', greetings);

    putHuntGroupVoicemailGreeting(huntGroupId, greeting)
      .then(() => {
        // TODO: success message.
      })
      .catch(() => {
        // TODO: failure message
      });
  };

  const onDeleteUserGreeting = (greeting) => {
    dispatch(setGreetingToRemove(greeting));
  };

  const onPinNumberFocus = () => {
    const pinNumber = getValues('pinNumber');
    if (!pinNumber || pinNumber === '••••') {
      setValue('pinNumber', '');
    }
  };

  const onPinNumberBlur = () => {
    const pinNumber = getValues('pinNumber');
    if (!pinNumber) {
      setValue('pinNumber', '••••');
    }
  };
  const setValueOnBlur = (input) => {
    const inputValue = getValues(input);
    if (!inputValue) {
      setValue(input.toString(), '');
    }
  };
  return (
    <form
      ref={optionsFormRef}
      onSubmit={handleSubmit(onSubmit)}
      data-testid={dataTestId}
    >
      {!isNewVoicemail && (
        <>
          <TableHeader>Greetings</TableHeader>
          <GreetingsContainer>
            {announcements?.map((item) => {
              return (
                <GreetingRow
                  key={item.greetingName}
                  greeting={item}
                  assignedAnnouncement={assignedAnnouncement}
                  defaultAnnouncement={defaultAnnouncement}
                  onDeleteUserGreeting={onDeleteUserGreeting}
                  onSetUserGreeting={onSetUserGreeting}
                />
              );
            })}
          </GreetingsContainer>
        </>
      )}
      <FormHeader data-testid="PlatformAdminUI-TeamVoiceSettings-VoiceMail-VoiceMailOptionsTitle">
        Voicemail options
      </FormHeader>
      <FormControlStyled component="fieldset" variant="outlined">
        <FormLabel
          component="legend"
          data-testid="PlatformAdminUI-TeamVoiceSettings-VoiceMail-VoiceMailPinLabel"
        >
          <Hint
            tool="VOICEMAIL PIN"
            tip="If a user is having trouble accessing their voicemail messages or if they are requesting a voicemail PIN change, you can update this user's voicemail PIN manually."
          />
        </FormLabel>
        <Controller
          name="pinNumber"
          control={control}
          defaultValue="••••"
          rules={{ pattern: regex.pin }}
          render={({ field }) => {
            return (
              <Pin
                {...field}
                onFocus={onPinNumberFocus}
                onBlur={onPinNumberBlur}
                disabled={!isVoicemailActive}
                error={errors.pinNumber?.type === 'pattern'}
                data-testid="PlatformAdminUI-TeamVoiceSettings-VoiceMail-VoiceMailPin"
              />
            );
          }}
        />
        <HelperText>
          {errors.pinNumber?.type === 'pattern' && 'Must be a 4 digit number.'}
        </HelperText>
      </FormControlStyled>
      <FormControlStyled component="fieldset" variant="outlined">
        <FormLabel
          component="legend"
          data-testid="PlatformAdminUI-TeamVoiceSettings-VoiceMail-VoiceMailToEmailLabel"
        >
          <Hint
            tool="VOICEMAIL TO EMAIL"
            tip="The email address where you would like the user to receive their voicemail message recordings."
          />
        </FormLabel>
        <Controller
          name="emailToCarbonCopy"
          control={control}
          defaultValue=""
          rules={{ pattern: regex.email }}
          render={({ field }) => {
            return (
              <Email
                {...field}
                error={errors.emailToCarbonCopy?.type === 'pattern'}
                disabled={!isVoicemailActive}
                onBlur={() => setValueOnBlur('emailToCarbonCopy')}
                data-testid="PlatformAdminUI-TeamVoiceSettings-VoiceMail-VoiceMailToEmail"
              />
            );
          }}
        />
        <HelperText>
          {errors.emailToCarbonCopy && 'Enter a valid email address.'}
        </HelperText>
      </FormControlStyled>
      <HorizontalFormControl component="fieldset" variant="outlined">
        <FormLabel
          component="legend"
          data-testid="PlatformAdminUI-TeamVoiceSettings-VoiceMail-VoiceMailAlertLabel"
        >
          <Hint
            tool="VOICEMAIL ALERT"
            tip="If you would like to receive an email notification that a new voicemail has been left, please check the box and enter your email address in the field."
          />
        </FormLabel>
        <Controller
          name="notificationEmailEnabled"
          control={control}
          defaultValue={false}
          render={({ field: { name, value, onChange, onBlur, ref } }) => {
            return (
              <Label
                control={
                  <CheckboxStyled
                    name={name}
                    ref={ref}
                    checked={value}
                    onChange={(e) => {
                      onChange(e);
                      setIsEmailEnabled(e.target.checked);
                    }}
                    onBlur={(e) => {
                      onBlur(e);
                      setEmailRequired(e.target.checked === true);
                    }}
                    disableRipple
                    data-testid="PlatformAdminUI-TeamVoiceSettings-VoiceMail-NewVoiceMailAlertCheckBox"
                  />
                }
                label="Alert for a new voicemail"
                data-testid="PlatformAdminUI-TeamVoiceSettings-VoiceMail-NewVoiceMailAlertLabel"
              />
            );
          }}
        />
        <Controller
          name="notificationEmail"
          control={control}
          defaultValue=""
          rules={{
            pattern: regex.email,
            required: emailRequired,
          }}
          render={({ field }) => {
            return (
              <GridStyled>
                <Email
                  {...field}
                  error={
                    (errors.notificationEmail?.type === 'pattern' ||
                      errors.notificationEmail?.type === 'required') &&
                    emailRequired
                  }
                  onBlur={() => setValueOnBlur('notificationEmail')}
                  disabled={!isEmailEnabled}
                  data-testid="PlatformAdminUI-TeamVoiceSettings-VoiceMail-NewVoiceMailAlert"
                />
                <HelperText>
                  {errors.notificationEmail &&
                    emailRequired &&
                    'Enter a valid email address.'}
                </HelperText>
              </GridStyled>
            );
          }}
        />
      </HorizontalFormControl>
    </form>
  );
};

OptionsForm.propTypes = {
  setOptionsHasErrors: func.isRequired,
  setOptionsIsDirty: func.isRequired,
  onSubmit: func.isRequired,
  huntGroupId: string,
  isVoicemailActive: bool,
};

export default OptionsForm;
